import ImageManagementService from "../services/ImageManagementService";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import imageCompression from 'browser-image-compression';
import { heicTo } from "heic-to";
const maxImageMB = 10;

// Concurrency limiter for image compression.
//
// inputFilter fires once per selected file and each call kicks off its own
// imageCompression (useWebWorker: true). With :multiple and no cap, selecting
// N files starts N compressions at once, all competing for CPU and holding the
// decoded bitmap in memory simultaneously (a 10MB JPEG is ~80MB as RGBA). That
// both slows every compression down and can hang/crash a mobile tab on large
// batches. This module-scoped queue caps how many run concurrently across the
// whole tab (both PhotoSelector and PhotoUploader share this mixin), so peak
// memory stays bounded regardless of batch size.
const MAX_CONCURRENT_COMPRESSIONS = 3;
let activeCompressions = 0;
const compressionQueue = [];

function pumpCompressionQueue() {
  while (activeCompressions < MAX_CONCURRENT_COMPRESSIONS && compressionQueue.length) {
    const job = compressionQueue.shift();
    activeCompressions++;
    imageCompression(job.file, job.options)
      .then(job.resolve, job.reject)
      .finally(function () {
        activeCompressions--;
        pumpCompressionQueue();
      });
  }
}

function runCompression(file, options) {
  return new Promise(function (resolve, reject) {
    compressionQueue.push({ file: file, options: options, resolve: resolve, reject: reject });
    pumpCompressionQueue();
  });
}

export default {
  data() {
    return {
        profile: this.$store.state.user.user,
        maxSize: 1048576 * maxImageMB,
        allowedExtensions: ["jpg", "jpeg", "png", "heic"],
        isBusy: false,
        // Number of images whose compression/read hasn't finished yet, i.e.
        // whose ImageFile (base64 payload) is not yet populated. Reactive, so
        // the Save button can disable itself while > 0. Incremented in
        // inputFilter before the async work starts and decremented when the
        // payload is ready or the work fails. See the silent-drop fix below.
        pendingCompressions: 0
    }
  },
  computed: {      
    nextSync() {
      dayjs.extend(relativeTime);
      var today = dayjs().format("MM/DD/YYYY");
      var syncTimes = [
        `${today} 09:30:00 AM`,
        `${today} 01:30:00 PM`,
        `${today} 04:30:00 PM`,
        `${today} 07:00:00 PM`,
      ]
        .map(function (s) {
          return dayjs(s, "hh:mm:ss A");
        })
        .sort(function (m) {
          return m.valueOf();
        })
        .find(function (m) {
          return m.isAfter();
        });

      if (syncTimes) {
        return `Images will sync ${syncTimes.fromNow()} at ${syncTimes.format(
          "hh:mm:ss A"
        )}.`;
      }
      // "Image Saved: undefined" toast fix: every sync time for today has
      // already passed (it's after 7:00 PM). The next sync is tomorrow's
      // 9:30 AM run. Return a sensible message instead of falling off the
      // end of the function with an implicit undefined return.
      var tomorrow = dayjs().add(1, "day");
      var tomorrowFirstSync = dayjs(
        `${tomorrow.format("MM/DD/YYYY")} 09:30:00 AM`,
        "hh:mm:ss A"
      );
      return `Images will sync ${tomorrowFirstSync.fromNow()} at ${tomorrowFirstSync.format(
        "hh:mm:ss A"
      )}.`;
    },
    isMobile() {
      //https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
      var hasTouchScreen = false;
      if ("maxTouchPoints" in navigator && window.innerWidth <= 768) {
        hasTouchScreen = navigator.maxTouchPoints > 0;
      } else if ("msMaxTouchPoints" in navigator) {
        hasTouchScreen = navigator.msMaxTouchPoints > 0;
      } else {
        var mQ = window.matchMedia && matchMedia("(pointer:coarse)");
        if (mQ && mQ.media === "(pointer:coarse)") {
          hasTouchScreen = !!mQ.matches;
        } else if ("orientation" in window) {
          hasTouchScreen = true; // deprecated, but good fallback
        } else {
          // Only as a last resort, fall back to user agent sniffing
          var UA = navigator.userAgent;
          hasTouchScreen =
            /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
            /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
        }
      }
      return hasTouchScreen;
    },
  },
  methods: {
    dateModified(date) {
      dayjs.extend(relativeTime);
      return date ? dayjs(date).fromNow() : "N/A";
    },
    getImageSource(image) {
      return image && !this.croppedImage ? image.BlobImageUrl || image.blob : this.croppedImage;
    },
    async inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile && !/\.(jpeg|jpg|png|heic|jfif)$/i.test(newFile.name)) {
        alert("Please select a supported image type to upload. Only .jpeg, .jpg, .png and .heic files are allowed. ");
        return prevent();
        }
        if (newFile.size > this.maxSize) {
            alert(`File size exceeds the limit (${maxImageMB}MB)`);
            newFile.error = 'Size limit exceeded';
            return prevent();
        }

        
      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        // Create blob and thumb fields BEFORE any `await` below.
        //
        // vue-upload-component invokes this filter synchronously and does NOT
        // wait for the returned Promise. The moment inputFilter hits the
        // `await heicTo(...)` call in the HEIC branch, execution suspends and
        // control returns to vue-upload-component, which pushes newFile into
        // the files array. Vue's Observer runs on newFile at that moment and
        // only makes the properties that EXIST right now reactive. Any
        // property added AFTER the await (via a plain assignment) is a
        // non-reactive "extra" — `<img :src="file.thumb">` will not update
        // when it's set later.
        //
        // Initializing `blob` AND `thumb` up front — while still in the
        // synchronous portion — makes both reactive so later assignments
        // propagate to the template. This is why the preview worked for JPG
        // (no await hit, whole function ran in one tick) but not HEIC
        // (awaited heicTo suspended before thumb was initialized).
        newFile.blob = "";
        newFile.thumb = "";
        let URL = window.URL || window.webkitURL;

        // Silent-drop fix: mark this file as "not ready" for the entire window
        // in which its ImageFile payload is still being produced. This must be
        // incremented BEFORE the `await heicTo(...)` below, because that await
        // suspends inputFilter and hands control back to vue-upload-component,
        // which immediately makes the file selectable/savable. Capturing `self`
        // (the component) lets the async .then()/.catch() callbacks below —
        // whose own `this` is not the component — adjust the reactive counter.
        const self = this;
        self.pendingCompressions++;

          // fileForCompression is what imageCompression will operate on.
          // HEIC is not decodable by Canvas (which browser-image-compression uses),
          // so for HEIC we first convert to JPEG and compress that instead.
          let fileForCompression = newFile.file;

          // heicTo can throw on a corrupt/unsupported HEIC. Because we've
          // already incremented pendingCompressions, an unhandled throw here
          // would leak the counter and disable Save forever — so release it
          // and drop the file on failure, matching the compression-failure
          // handling below.
          try {
              if (URL && URL.createObjectURL) {
                  const extension = newFile.name.split('.').pop().trim().toLowerCase();

                  if (extension == "heic") {
                      const jpegBlob = await heicTo({
                          blob: newFile.file,
                          type: "image/jpeg",
                          quality: 0.8 // 0 to 1
                      });
                      newFile.blob = URL.createObjectURL(jpegBlob);
                      fileForCompression = new File(
                          [jpegBlob],
                          newFile.name.replace(/\.heic$/i, '.jpg'),
                          { type: 'image/jpeg' }
                      );
                  } else {
                      newFile.blob = URL.createObjectURL(newFile.file);
                  }
              }
          } catch (error) {
              console.error('HEIC conversion failed:', error);
              alert('Could not process this image. Please try a different file.');
              self.pendingCompressions--;
              if (self.$refs.upload) { self.$refs.upload.remove(newFile); }
              return;
          }

      var options = {
        maxSizeMB: 5,
        maxWidthOrHeight: 800,
        useWebWorker: true
      }
      // runCompression throttles how many compressions run at once (see the
      // module-scoped limiter above). The promise is still not awaited by
      // inputFilter — the file is populated asynchronously — but the
      // pendingCompressions counter keeps Save disabled until it lands, and
      // the save-time guard in PhotoUploader.uploadImages is the hard backstop.
      runCompression(fileForCompression, options)
        .then(function (compressedFile) {
          var reader = new window.FileReader();
          // Use onload (success only), not onloadend (fires on success AND
          // error): pairing onloadend with onerror would double-decrement the
          // counter and dereference a null reader.result on a failed read.
          reader.onload = function () {
            var base64data = reader.result.replace(/^data:image\/(png|jpg|jpeg);base64,/, "");
            newFile.ImageFile = base64data;
            // Payload is ready — this file no longer blocks Save.
            self.pendingCompressions--;
          }
          reader.onerror = function () {
            // Read failed: ImageFile will never populate. Release the counter
            // and drop the file so it can't block Save forever (mirrors the
            // compression-failure path below).
            console.error('Image read failed:', reader.error);
            alert('Could not process this image. Please try a different file.');
            self.pendingCompressions--;
            if (self.$refs.upload) { self.$refs.upload.remove(newFile); }
          }
          reader.readAsDataURL(compressedFile);
        })
        .catch(function (error) {
          console.error('Image compression failed:', error);
          alert('Could not process this image. Please try a different file.');
          // Release the counter and remove the stuck file — otherwise its
          // empty ImageFile would keep Save disabled/blocked indefinitely.
          self.pendingCompressions--;
          if (self.$refs.upload) { self.$refs.upload.remove(newFile); }
        });

        // Thumbnails: assign the renderable blob URL as the preview source.
        // (thumb was already initialized up top so this write is reactive
        // even when the HEIC await suspended inputFilter and Vue had
        // already observed newFile by the time we get here.)
        newFile.thumb = newFile.blob || "";

        
        newFile.IsNewFile = true;
        newFile.IsUploaded = false;             
      }
    },
    toastedAlert(message, type = "success") {
      this.$toasted.show(message, {
        type: type,
        theme: "toasted-primary",
        duration: 3500,
        position: 'top-center'
      });
    },
      deleteSelected(file) {

          if (file && file.BlobImageUrl) {
              this.loading = true;
          file.Status = "D";
          file.IsNew = false;
          file.IsUpdated = false;
          file.IsDeleted = true;
          file.ModifiedBy = this.profile.name;
          file.UserEmail = this.profile.email;
            if (file) {
                ImageManagementService.deleteExistingImage(file, this.profile).then((response) => {
                    this.toastedAlert("Image Deleted!");
                    this.machine.ImageData = [];
                    this.machine.ImageData = response.data[1].ImageData;
                    response.data[0].ImageData.forEach((item) => {
                        this.archivedImages.push(item);
                    });
                    // A successful delete is itself a save; clear the unsaved-
                    // changes flag for consistency with the multi-delete path.
                    this.unsavedChanges = false;
                }).finally(() => {
                    this.loading = false;

                });
            }
            
        } else if (file && !file.BlobImageUrl) {
          this.photos = this.photos.filter((item) => item.id !== file.id);
        } else {
          alert("No photos have been selected.");
        }
    },
    deleteSelectedImages(images) {
      // Caller (deleteMultipleImages in PhotoUploader) is responsible for confirming
      // before calling this. Do not prompt again here — a second confirm would cause
      // the grid to be out of sync with the server.
      if (!images || !images.length) return;
      var allImages = []
      this.loading = true;

      images.forEach((image) => {
          image.Status = "D";
          image.IsNew = false;
          image.IsUpdated = false;
          image.IsDeleted = true;
          image.ModifiedBy = this.profile.name;
          image.UserEmail = this.profile.email;

          allImages.push(image)
      });


          ImageManagementService.deleteExistingImage(allImages, this.profile).then((response) => {
              if (response.status === 200) {
                  this.toastedAlert("Image Deleted!");
                  this.machine.ImageData = [];
                  this.machine.ImageData = response.data[1].ImageData;
                  response.data[0].ImageData.forEach((item) => {
                      this.archivedImages.push(item);
                  });
                  // A successful delete is itself a save; clear the unsaved-
                  // changes flag so the "YOU HAVE UNSAVED CHANGES" banner
                  // doesn't linger after bulk-delete. The machine watcher
                  // previously set this flag spuriously on every nested
                  // mutation, so explicitly resetting it here makes the
                  // post-delete state unambiguous.
                  this.unsavedChanges = false;
              } else {
                  this.toastedAlert("Error","error");
              }    
            }).finally(() => {
                this.loading = false;
            });
    },
    //called for sort function
      uploadExistingImageData(img, silent = false) {
      // `silent` (default false): when uploadImages chains this call before
      // a subsequent uploadNewImageData (sort + new-image both pending), the
      // chained caller passes silent=true to suppress this save's toast so
      // the user sees only ONE "Image Saved!" at the end of the chain
      // instead of two.
      //
      // Returning the promise chain lets uploadImages sequence the next save
      // after this one resolves. Without that, both saves would race against
      // the same Cosmos record and the new-image write — typically arriving
      // second — would overwrite the sort save's update with a stale read,
      // leaving Cosmos sort-pre and blob slots sort-post. That's exactly
      // the "duplicate image appears, original disappears" symptom QA
      // reported for the upload-then-reorder workflow.
      this.loading = true;
      var images = []

          img.forEach((item) => {

              item.ImageFile = !item.BlobImageUrl ? item.ImageFile : null;
              item.IsNew = false;
              item.IsUpdated = true;

              item.ModifiedBy = this.profile.name;
              item.UserEmail = this.profile.email;
              
              images.push(item);
          });

          return ImageManagementService.updateExistingImage(images, this.profile)
              .then((response) => {
                  if (!silent) {
                      this.toastedAlert(`Image Saved! ${this.nextSync}`);
                  }
                  // Edit-shows-wrong-image bug fix: previously this save path mutated
                  // items in imagesForCards in place (ImageFile=null, IsUpdated=true,
                  // etc.) and then dropped the server response on the floor. That left
                  // the client with an ad-hoc mix of drag-ordered references, Resorted
                  // flags, and mutated fields — and in that state, Bootstrap's modal
                  // lookup via data-target=#image-editor-${id} was occasionally
                  // resolving to the wrong modal ("first image opens last image", etc.).
                  // Every other save path (delete, multi-delete, new upload, edit-update)
                  // already rehydrated machine.ImageData from the server response,
                  // which the deep `machine` watcher in PhotoUploader then copies into
                  // imagesForCards — the same clean rebuild a page refresh produces.
                  // Doing the same thing here makes resort-save behave identically to
                  // the other save paths and eliminates the stale-state Edit bug.
                  if (response
                      && response.data
                      && Array.isArray(response.data)
                      && response.data[1]
                      && Array.isArray(response.data[1].ImageData)
                      && this.machine) {
                      this.machine.ImageData = [];
                      this.machine.ImageData = response.data[1].ImageData;
                  }
              })
              .finally(() => {
                  // When chained (silent=true), DON'T release the busy/loading
                  // state here — the next save in the chain is about to take
                  // over and needs to see them still set. The chained tail
                  // (uploadNewImageData) will reset everything in its own
                  // .finally().
                  if (!silent) {
                      this.loading = false;
                      this.isBusy = false;
                      this.unsavedChanges = false;
                  }
              });

    },
      uploadNewImageData(img) {
      this.loading = true;
      var images = [];
      
      img.forEach((item) => {
        item.BlobImageUrl = "";
        item.StockNumber =
          this.selectedMachine && this.selectedMachine.StockNumber
            ? this.selectedMachine.StockNumber
            : this.machine.StockNumber;
        item.IsNew = true;
        item.IsUpdated = false;      
        item.Status = "N";
        item.ModifiedBy = this.profile.name;
        item.UserEmail = this.profile.email;
        item.id = null;  
        //Sets local properties to block duplicate uploads.     
        item.IsNewFile = false;
        item.IsUploaded = true;

        images.push(item)
      })                

      // Returns the underlying axios promise so callers (e.g. uploadImages
      // when chaining a sort save + new-image save) can sequence work after
      // the response lands. The .finally() resets loading/isBusy regardless.
      return ImageManagementService.addNewImage(images, this.profile)
        .then((response) => {
          this.toastedAlert(`Image Saved! ${this.nextSync}`);
          // Image-triplication bug fix: clear this.photos BEFORE installing the
          // server response onto this.machine.ImageData. The machine watcher
          // (with deep: true for Bug #56) fires on every nested assignment
          // below, and any leftover local photo objects here could end up
          // duplicated into imagesForCards. Clearing photos first ensures a
          // clean swap from "local preview" to "server-backed records."
          this.photos = [];
          this.machine.ImageData= [];
          this.machine.ImageData = response.data[1].ImageData;
          // Image-duplication bug fix: do NOT push response.data[0].ImageData
          // into machine.ImageData. The server returns two equipment entries:
          // data[0] is the server-processed client input (which, for a new
          // upload, contains the newly saved image with its server-assigned id),
          // and data[1] is the fresh Cosmos state (which ALSO contains that
          // same image with the same id). Pushing data[0] on top of data[1]
          // would duplicate every new image by id.
          //
          // data[1] alone is authoritative for the active image grid. If
          // archive/other bookkeeping from data[0] is needed later, that should
          // be handled by a targeted, id-deduplicated merge — not a blanket
          // push into machine.ImageData.
        }).finally(() => {
            this.loading = false,
                this.photos = []
            this.isBusy = false;
            this.unsavedChanges = false;
        }); 
    },
      updateImageData(img, croppedImage) {
      this.loading = true;
      // Shallow copy to avoid mutating the shared ImageManagementService.defaultImageData reference.
      let imageCopy = { ...ImageManagementService.defaultImageData };

      if (croppedImage) {
        imageCopy.ImageFile = croppedImage.substring(
          croppedImage.indexOf(",") + 1
        );
      }
      imageCopy.StockNumber = img.StockNumber;
      imageCopy.IsNew = false;
      imageCopy.IsUpdated = true;
      imageCopy.Status = "U";
      imageCopy.SortOrder = img.PrevSortOrder; //FIX to retain cropped image : for some reason sortorder was diff from prevsortorder
      imageCopy.PrevSortOrder = img.PrevSortOrder;
      imageCopy.id = img.id;
      imageCopy.ModifiedBy = this.profile.name;
      imageCopy.UserEmail = this.profile.email;

        // Bug #57 fix: return the promise so the caller (PhotoEditor) can
        // await completion and the main page can toggle its isBusy/loading
        // state around the actual API call.
        return ImageManagementService.updateExistingCroppedImage(imageCopy, this.profile)
        .then(() => {
          this.toastedAlert(`Image Updated! ${this.nextSync}`);
        })
        .finally(() => (this.loading = false));
    },
    restoreImage(val) {
      this.loading = true;
      // Shallow copy to avoid mutating the shared ImageManagementService.defaultImageData reference.
      let imageCopy = { ...ImageManagementService.defaultImageData };
      
      if (confirm("Are you sure that you want to restore this image?")) {
        imageCopy.BlobImageUrl = val.BlobImageUrl;
        imageCopy.StockNumber = val.StockNumber;
        imageCopy.id = val.id;
        imageCopy.IsArchived = true;
        imageCopy.IsNew = true;
        imageCopy.IsDeleted = false;
        imageCopy.Status = "N";
        imageCopy.ModifiedBy = this.profile.name;
        imageCopy.UserEmail = this.profile.email;

        ImageManagementService.restoreImage(imageCopy, this.profile)
          .then((response) => {
            this.toastedAlert("Image Restored!");
            this.imagesForCards = [];                    
            
            response.data[0].ImageData.forEach((item) => {
              this.imagesForCards.push(item);
              this.archivedImages.forEach((archivedImage) => {
                if (archivedImage.id === item.id) {
                  this.archivedImages.splice(item, 1);
                }
              });              
            });  
            
            response.data[1].ImageData.forEach((item) => {
              if (item.Status != "D") {
                this.imagesForCards.push(item);
              }
             });
          }).finally(() => (this.loading = false));
         
          }
    },
  },
  watch: {
    photos: {
      handler: function() {
        this.photos.forEach((element) => {
          const img = new Image();
          img.onload = function() {
            element.height = this.height;
            element.width = this.width;
          };
          img.src = element.blob;
        });
      },
      deep: true,
    },
  },
};